import { sendMessageToParent } from '@/utils/iframeMessaging';
import { EventMap } from '@/utils/analyticsEventTypes';

export interface SaveImageChangesParams {
  imageUrl: string;
  landingPageId: string;
  xPath: string;
}

export interface UploadAndReplaceImageParams {
  imageElement: HTMLImageElement;
  file: File;
  landingPageId: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  track?: <K extends keyof EventMap>(eventName: K, properties: EventMap[K]) => void;
  setIsUploadingImage: (uploading: boolean) => void;
  getXPath: (element: HTMLElement, stopAt?: HTMLElement) => string;
}

export const saveImageChanges = async ({
  imageUrl,
  landingPageId,
  xPath,
}: SaveImageChangesParams): Promise<any> => {
  const hostname = typeof window !== 'undefined' ? window.location.origin : '';

  const response = await fetch('/api/landing-page/update-html', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      landingPageId,
      imageUrl,
      xPath,
      hostname,
      type: 'image-replacement',
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to replace image');
  }

  return response.json();
};

export const uploadAndReplaceImage = async ({
  imageElement,
  file,
  landingPageId,
  containerRef,
  track,
  setIsUploadingImage,
  getXPath,
}: UploadAndReplaceImageParams): Promise<void> => {
  if (!imageElement || !file || !containerRef.current) return;

  // Validate file
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
  ];

  if (file.size > maxSize) {
    alert('File size must be less than 10MB');
    return;
  }

  if (!allowedTypes.includes(file.type)) {
    alert('Please select a valid image file (JPEG, PNG, GIF, WebP, SVG)');
    return;
  }

  const contentRoot = containerRef.current?.firstElementChild as HTMLElement | null;
  const xPath = getXPath(imageElement, contentRoot || undefined);
  const originalSrc = imageElement.src;

  // Track image replacement attempt
  if (track) {
    track('Landing Page Image Replace', {
      landingPageId,
      elementTag: 'img',
      originalSrc,
      fileSize: file.size,
      fileType: file.type,
      timestamp: new Date().toISOString(),
      status: 'attempted',
    });
  }

  setIsUploadingImage(true);

  try {
    // Upload to Firebase Storage
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const uniqueFileName = `landing_page_${landingPageId}_${timestamp}_${randomString}.${fileExtension}`;

    // Import Firebase storage functions
    const { storage } = await import('@/lib/firebase/firebase');
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

    const storageRef = ref(storage, `uploads/${uniqueFileName}`);
    await uploadBytes(storageRef, file);
    const newImageUrl = await getDownloadURL(storageRef);

    // Save the image replacement
    await saveImageChanges({
      imageUrl: newImageUrl,
      landingPageId,
      xPath,
    });

    // Track successful image replacement
    if (track) {
      track('Landing Page Image Replace', {
        landingPageId,
        elementTag: 'img',
        originalSrc,
        newSrc: newImageUrl,
        fileSize: file.size,
        fileType: file.type,
        success: true,
        timestamp: new Date().toISOString(),
        status: 'completed',
      });
    }

    // Notify parent that image was replaced
    sendMessageToParent('REPLACE_IMAGE', {
      elementTag: 'img',
      originalSrc,
      newSrc: newImageUrl,
      xPath,
      landingPageId,
    });
  } catch (error) {
    console.error('Failed to upload and replace image:', error);

    // Track failed image replacement
    if (track) {
      track('Landing Page Image Replace', {
        landingPageId,
        elementTag: 'img',
        originalSrc,
        fileSize: file.size,
        fileType: file.type,
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false,
        timestamp: new Date().toISOString(),
        status: 'failed',
      });
    }

    alert(`Failed to replace image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    setIsUploadingImage(false);
  }
};

export const isImageElement = (
  element: HTMLElement,
  imageCache: React.MutableRefObject<WeakMap<HTMLImageElement, boolean>>
): element is HTMLImageElement => {
  // Check cache first
  const cached = imageCache.current.get(element as HTMLImageElement);
  if (cached !== undefined) return cached;

  // Only img tags are replaceable
  if (element.tagName !== 'IMG') {
    imageCache.current.set(element as HTMLImageElement, false);
    return false;
  }

  const imgElement = element as HTMLImageElement;

  // Must have a src attribute
  if (!imgElement.src) {
    imageCache.current.set(imgElement, false);
    return false;
  }

  // Skip if it's a tiny image (likely an icon or tracking pixel)
  if (imgElement.width < 50 || imgElement.height < 50) {
    imageCache.current.set(imgElement, false);
    return false;
  }

  const result = true;
  imageCache.current.set(imgElement, result);
  return result;
};

export const handleImageClick = (
  imageElement: HTMLImageElement,
  isEditing: boolean,
  isUploadingImage: boolean,
  uploadAndReplaceImageFn: (imageElement: HTMLImageElement, file: File) => Promise<void>
): void => {
  if (isEditing || isUploadingImage) return;

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml';
  input.onchange = e => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      uploadAndReplaceImageFn(imageElement, file);
    }
  };
  input.click();
};
